import { useState } from "react";
import type { Account, ExpenseInput } from "../../types";
import { api } from "../../lib/api";

type Frequency = "weekly" | "monthly" | "yearly";

/**
 * The entry form's "Make this a recurring bill" option: once the entry itself is saved, it becomes
 * the first paid cycle of a new bill. Offered on the same terms as loan tracking — only a brand-new
 * manual entry, never an edit or a review.
 */
export function useRecurringEntry(canMakeRecurring: boolean) {
  const [makeRecurring, setMakeRecurring] = useState(false);
  const [frequency, setFrequency] = useState<Frequency>("monthly");
  const [reminderDaysBefore, setReminderDaysBefore] = useState("");
  const [error, setError] = useState("");

  /** Creates the bill anchored to the saved entry, when asked for. Never throws: the entry is
   * already saved and verified, so a failed bill on top of it is its own problem to surface. */
  async function create(transactionId: string | undefined, input: ExpenseInput, account: Account | undefined) {
    if (!canMakeRecurring || !makeRecurring || !transactionId) return;
    try {
      await api("bills/create", {
        requestId: crypto.randomUUID(),
        sourceTransactionId: transactionId,
        type: input.type === "Income" ? "Income" : "Expense",
        name: input.title || input.category,
        accountId: input.accountId,
        payeeId: input.payeeId,
        category: input.category,
        subcategory: input.subcategory,
        amount: input.amount,
        currency: account?.currency || "MVR",
        frequency,
        reminderDaysBefore,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the bill.");
    }
  }

  return {
    error,
    create,
    fields: {
      canMakeRecurring, makeRecurring, setMakeRecurring,
      frequency, setFrequency, reminderDaysBefore, setReminderDaysBefore,
    },
  };
}
